import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Pencil, Plus, Building2 } from 'lucide-react';
import { FormSheet } from '@/components/common/FormSheet';
import { FormField } from '@/components/common/FormField';
import { DialogEyebrow } from '@/components/common/DialogPrimitives';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { vendorsService, type VendorCreatePayload } from '@/services/vendors';
import type { Vendor, VendorStatus } from '@/types/vendors';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vendor: Vendor | null;
}

const EMPTY: VendorCreatePayload = {
  name: '',
  code: '',
  email: '',
  phone: '',
  gstin: '',
  pan: '',
  externalRef: '',
  status: 'ACTIVE',
};

export function VendorFormSheet({ open, onOpenChange, vendor }: Props) {
  const qc = useQueryClient();
  const isEdit = !!vendor;
  const [form, setForm] = useState<VendorCreatePayload>(EMPTY);

  useEffect(() => {
    if (!open) return;
    if (vendor) {
      setForm({
        name: vendor.name,
        code: vendor.code,
        email: vendor.email ?? '',
        phone: vendor.phone ?? '',
        gstin: vendor.gstin ?? '',
        pan: vendor.pan ?? '',
        externalRef: vendor.externalRef ?? '',
        status: vendor.status,
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, vendor]);

  const mutation = useMutation({
    mutationFn: async () => {
      const payload: VendorCreatePayload = {
        ...form,
        name: form.name.trim(),
        code: emptyToUndef(form.code),
        email: emptyToUndef(form.email),
        phone: emptyToUndef(form.phone),
        gstin: form.gstin?.trim() ? form.gstin.trim().toUpperCase() : undefined,
        pan: form.pan?.trim() ? form.pan.trim().toUpperCase() : undefined,
        externalRef: emptyToUndef(form.externalRef),
      };
      if (isEdit && vendor) {
        const { code: _code, ...patch } = payload;
        return vendorsService.update(vendor.id, patch);
      }
      return vendorsService.create(payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Vendor updated' : 'Vendor created');
      qc.invalidateQueries({ queryKey: ['vendors'] });
      onOpenChange(false);
    },
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    mutation.mutate();
  };

  const HeroIcon = isEdit ? Pencil : Plus;

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      headerSlot={
        <div className="flex items-start gap-4">
          <span
            aria-hidden="true"
            className="grid h-14 w-14 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 text-primary ring-1 ring-inset ring-primary/15"
          >
            {isEdit && vendor ? (
              <Building2 className="h-6 w-6" />
            ) : (
              <HeroIcon className="h-6 w-6" />
            )}
          </span>

          <div className="min-w-0 flex-1 space-y-1.5">
            <DialogEyebrow>
              {isEdit ? `Edit vendor · ${vendor?.code}` : 'New vendor'}
            </DialogEyebrow>
            <DialogTitle className="text-[18px] font-semibold leading-tight tracking-tight">
              {isEdit ? vendor?.name : 'Create vendor'}
            </DialogTitle>

            {isEdit && vendor ? (
              <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                <Badge variant="muted" className="font-mono text-[10px]">
                  {vendor.code}
                </Badge>
                {vendor.gstin && (
                  <Badge variant="muted" className="font-mono text-[10px]">
                    GSTIN
                  </Badge>
                )}
                {vendor.pan && (
                  <Badge variant="muted" className="font-mono text-[10px]">
                    PAN
                  </Badge>
                )}
                <Badge
                  variant={vendor.status === 'ACTIVE' ? 'success' : 'muted'}
                  className="text-[10px]"
                >
                  {vendor.status === 'ACTIVE' ? 'active' : 'archived'}
                </Badge>
              </div>
            ) : (
              <DialogDescription className="text-[12.5px] leading-relaxed">
                Add a vendor record. Vendors become available for purchase orders + supplier
                receipt references immediately.
              </DialogDescription>
            )}
          </div>
        </div>
      }
      title=""
      submitLabel={isEdit ? 'Save changes' : 'Create vendor'}
      onSubmit={onSubmit}
      isSubmitting={mutation.isPending}
      error={mutation.error}
      size="3xl"
    >
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Name" htmlFor="name" required>
          <Input
            id="name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Acme Manufacturing Pvt Ltd"
            required
            autoComplete="off"
          />
        </FormField>
        <FormField
          label="Code"
          htmlFor="code"
          hint={isEdit ? 'Immutable' : 'Auto-generated when blank (VND-XXXXXX)'}
        >
          <Input
            id="code"
            value={form.code ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
            placeholder="VND-AB12CD"
            disabled={isEdit}
            autoComplete="off"
            className="font-mono"
          />
        </FormField>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField label="Email" htmlFor="email">
          <Input
            id="email"
            type="email"
            value={form.email ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="ops@acme.example"
            autoComplete="off"
          />
        </FormField>
        <FormField label="Phone" htmlFor="phone">
          <Input
            id="phone"
            value={form.phone ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            placeholder="+91 98765 43210"
            autoComplete="off"
          />
        </FormField>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField label="GSTIN" htmlFor="gstin" hint="15-char India GST number. Required for ITC eligibility.">
          <Input
            id="gstin"
            value={form.gstin ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, gstin: e.target.value.toUpperCase() }))}
            placeholder="29AAAAA0000A1Z5"
            maxLength={15}
            autoComplete="off"
            className="font-mono"
          />
        </FormField>
        <FormField label="PAN" htmlFor="pan" hint="10-char India PAN. Used for TDS computation.">
          <Input
            id="pan"
            value={form.pan ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, pan: e.target.value.toUpperCase() }))}
            placeholder="ABCDE1234F"
            maxLength={10}
            autoComplete="off"
            className="font-mono"
          />
        </FormField>
      </div>

      <FormField label="External reference" htmlFor="externalRef" hint="Vendor ID in your accounting / procurement system.">
        <Input
          id="externalRef"
          value={form.externalRef ?? ''}
          onChange={(e) => setForm((f) => ({ ...f, externalRef: e.target.value }))}
          placeholder="acme-supplier-2310"
          autoComplete="off"
        />
      </FormField>

      {isEdit && (
        <FormField label="Status" htmlFor="status">
          <Select
            value={form.status ?? 'ACTIVE'}
            onValueChange={(v) => setForm((f) => ({ ...f, status: v as VendorStatus }))}
          >
            <SelectTrigger id="status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ACTIVE">Active</SelectItem>
              <SelectItem value="INACTIVE">Archived</SelectItem>
            </SelectContent>
          </Select>
        </FormField>
      )}
    </FormSheet>
  );
}

function emptyToUndef(s: string | undefined | null): string | undefined {
  const t = s?.trim();
  return t ? t : undefined;
}
